import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FRASI = {
  home: [
    "Psst... le novità di oggi ti aspettano nel catalogo!",
    "Una bustina al giorno toglie il mulligan di torno!",
    "Hai una decklist? Il Deck Builder te la quota in un lampo!",
    "In negozio i PUNTI WANTED valgono doppio. Parola di Needle!",
    "Buoni pull! Che la rara sia con te.",
  ],
  profilo: [
    "Che bello rivederti! Dai un'occhiata ai tuoi PUNTI WANTED.",
    "Il tuo forziere ti aspetta: ogni punto è un piccolo tesoro.",
    "Torna presto: le carte rare non si fanno attendere!",
  ],
  admin: [
    "Buon lavoro, boss! Il caveau è in ottime mani.",
    "Tutto sotto controllo: Needle veglia sulle scorte.",
    "Una puntina qua, una là... e l'inventario è in ordine!",
  ],
  faq: [
    "Le risposte le so tutte. Quasi tutte. Vabbè, punzecchia le domande!",
    "Domanda trabocchetta? Quelle le riservo agli avversari.",
    "Se non trovi la risposta, scrivici: prometto di non pungere.",
  ],
};

// Frasi stagionali: finestre [meseInizio, giornoInizio, meseFine, giornoFine] (incluse, anno corrente)
const STAGIONI = [
  { da: [12, 8], a: [12, 26], frasi: [
    "Buon Natale! Nel caveau è pieno di regali per collezionisti...",
    "Dolci, panettone e bustine: il mio Natale ideale. E il tuo?",
  ]},
  { da: [12, 27], a: [1, 6], frasi: [
    "Buon anno! Che il 1° gennaio ti porti una Secret Rare.",
  ]},
  { da: [10, 26], a: [11, 2], frasi: [
    "Brrr... ad Halloween anche le carte comuni fanno paura. Quasi.",
    "Dolcetto o booster? Io scelgo sempre booster.",
  ]},
];

const CHIAVE = "mascotte-nascosta";
const GIORNI_NASCOSTA = 7; // chi la chiude con la X non la rivede per una settimana, poi torna

function inStagione(s, oggi) {
  const [mi, gi] = s.da, [mf, gf] = s.a;
  const d = oggi.getMonth() + 1, g = oggi.getDate();
  if (mi <= mf) return (d > mi || (d === mi && g >= gi)) && (d < mf || (d === mf && g <= gf));
  // finestra a cavallo d'anno (es. 27 dic → 6 gen)
  return (d > mi || (d === mi && g >= gi)) || (d < mf || (d === mf && g <= gf));
}

export default function Mascotte() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const gruppo = pathname === "/" ? "home" : pathname.startsWith("/profilo") ? "profilo" : pathname.startsWith("/admin") ? "admin" : pathname.startsWith("/faq") ? "faq" : null;
  const [nascosta, setNascosta] = useState(() => {
    try {
      const ts = parseInt(localStorage.getItem(CHIAVE) || "", 10);
      return Number.isFinite(ts) && Date.now() - ts < GIORNI_NASCOSTA * 864e5;
    } catch { return false; }
  });
  const [extra, setExtra] = useState([]); // frasi speciali: {testo, link?}

  // Saluto per nome ai clienti abituali: una sola volta per sessione, poi rotazione normale
  const [saluto, setSaluto] = useState(null);
  useEffect(() => {
    if (saluto || !user || user.role === "admin" || !gruppo || gruppo === "admin") return;
    try { if (sessionStorage.getItem("needle-saluto-fatto") === "1") return; } catch {}
    const nome = (user.name || "").trim().split(/\s+/)[0];
    if (!nome) return;
    setSaluto(`Bentornato, ${nome}! Il caveau ti è mancato?`);
    try { sessionStorage.setItem("needle-saluto-fatto", "1"); } catch {}
  }, [user, saluto, gruppo]);

  useEffect(() => {
    if (gruppo !== "home" && gruppo !== "profilo") return;
    let alive = true;
    const frasi = [];
    // Stagioni (Natale, Halloween...)
    const oggi = new Date();
    for (const s of STAGIONI) if (inStagione(s, oggi)) frasi.push(...s.frasi.map((t) => ({ testo: t })));
    // Torneo / prerelease in arrivo entro 10 giorni
    api.get("/tournaments").then((r) => {
      if (!alive) return;
      const limite = new Date(); limite.setDate(limite.getDate() + 10);
      const prossimi = (r.data || []).filter((t) => !t.is_past && t.registration_state !== "annullato" && new Date(t.date) <= limite);
      if (prossimi.length) {
        const t = prossimi[0];
        const pre = (t.name || "").toLowerCase().includes("prerelease");
        frasi.push({ testo: pre
          ? `Prerelease in arrivo: ${t.name}! I kit volano, punzecchia il tasto iscrizioni!`
          : `Manca poco a ${t.name}! Hai già prenotato il tuo posto?`, link: "/tornei" });
      }
      // Carte in offerta: l'admin le marca col tag OFFERTA
      return api.get("/products", { params: { tag: "OFFERTA", limit: 5 } });
    }).then((r) => {
      if (!alive || !r) return;
      const offerte = r.data || [];
      if (offerte.length) {
        const p = offerte[Math.floor(Math.random() * offerte.length)];
        frasi.push({ testo: `Occhio: ${p.name} è in offerta nel caveau. Tocca e vola a vederla!`, link: `/prodotto/${p.id}` });
      }
      setExtra(frasi);
    }).catch(() => alive && setExtra(frasi));
    return () => { alive = false; };
  }, [gruppo]);

  const frase = useMemo(() => {
    if (!gruppo) return null;
    if (saluto) return { testo: saluto };
    // Le frasi speciali (stagione, tornei, offerte) pesano doppio rispetto a quelle base
    const pool = [...extra, ...extra, ...FRASI[gruppo].map((t) => ({ testo: t }))];
    return pool[Math.floor(Math.random() * pool.length)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gruppo, extra.length, saluto]);

  if (!gruppo || !frase) return null;

  const chiudi = () => {
    try { localStorage.setItem(CHIAVE, String(Date.now())); } catch {}
    setNascosta(true);
  };

  return (
    <AnimatePresence>
      {!nascosta && (
        <motion.div
          key="mascotte"
          initial={{ opacity: 0, y: 40, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.9 }}
          transition={{ type: "spring", stiffness: 260, damping: 22, delay: 0.6 }}
          className="fixed bottom-4 right-4 z-40 flex flex-col items-end pointer-events-none"
          data-testid="mascotte"
        >
          {/* Fumetto */}
          <div className="relative mb-2 mr-2 max-w-[220px] pointer-events-auto" data-testid="mascotte-fumetto">
            <div
              className={`rounded-2xl border border-red-500/30 bg-[#141416]/95 px-4 py-3 pr-8 shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-md ${frase.link ? "cursor-pointer transition-colors hover:border-red-500/60" : ""}`}
              onClick={frase.link ? () => navigate(frase.link) : undefined}
              role={frase.link ? "link" : undefined}
            >
              <p className="text-xs sm:text-sm leading-snug text-zinc-100">
                {frase.testo}
                {frase.link && <span className="ml-1 text-red-400" aria-hidden>→</span>}
              </p>
            </div>
            {/* codina del fumetto */}
            <div className="absolute -bottom-[7px] right-8 h-4 w-4 rotate-45 border-b border-r border-red-500/30 bg-[#141416]/95" aria-hidden />
            <button
              type="button"
              onClick={chiudi}
              aria-label="Chiudi mascotte"
              data-testid="mascotte-chiudi"
              className="absolute right-1.5 top-1.5 rounded-full p-1 text-zinc-500 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {/* Needle: dondolio leggero */}
          <motion.img
            src="/mascotte.png"
            alt="Needle, la mascotte di Wanted"
            data-testid="mascotte-img"
            className="w-16 sm:w-20 pointer-events-auto select-none drop-shadow-[0_6px_16px_rgba(220,38,38,0.35)]"
            animate={{ y: [0, -6, 0], rotate: [0, -4, 0, 4, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            draggable={false}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
